import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { LeftCard, RightCard } from '@sandtech-hub-monorepo/ui-kit';
import { useCognito } from '@sandtech-hub-monorepo/ui-auth-kit';

import {
  confirmSignUpTitle,
  signUpCTAQuestion,
  signUpCTAText,
} from '../../../../stringsManager';
import { ConfirmSignUpForm } from './components/ConfirmSignUpForm';
import {
  RefereeBottomCard,
  RefereeLeftCard,
} from '../SignUp/components/RefereeLeftCard';

export interface ConfirmSignUpProps {
  loginRoute: string;
  confirmSignUpCover: string;
  afterAutoSignIn: string;
  /**
   * Fired after a successful email signup confirmation (and the subsequent
   * autoSignIn, if the flow includes it). Social signup flows do not reach
   * this component — they go through CognitoProvider's onSocialSignIn.
   */
  onSignUpComplete?: () => void;
}

export const ConfirmSignUp = (props: ConfirmSignUpProps) => {
  const navigate = useNavigate();

  const { loading, confirmSignUp, autoSignIn } = useCognito();
  const [params] = useSearchParams();
  const referralCode = params.get('ref')?.trim();

  const handleConfirmSignUp = async (values: {
    email: string;
    confirmationCode: string;
  }) => {
    const confirmSignUpResult = await confirmSignUp({
      username: values.email,
      confirmationCode: values.confirmationCode,
    });

    if (confirmSignUpResult && confirmSignUpResult.isSignUpComplete) {
      if (confirmSignUpResult.nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
        // Call `autoSignIn` API to complete the flow
        const autoSignInResult = await autoSignIn();
        if (autoSignInResult) {
          if (
            autoSignInResult.nextStep.signInStep === 'DONE' ||
            autoSignInResult.isSignedIn
          ) {
            props.onSignUpComplete?.();
            toast.success(
              'You have successfully signed up. Welcome to the eHub!'
            );
            navigate({ pathname: props.afterAutoSignIn });
          }
        }
      } else {
        props.onSignUpComplete?.();
        toast.success(
          'You have successfully signed up. Login to access your account!'
        );
        navigate({ pathname: props.loginRoute, search: params.toString() });
      }
    }
  };

  return (
    <>
      {referralCode ? (
        <RefereeLeftCard />
      ) : (
        <LeftCard backgroundImage={props.confirmSignUpCover} />
      )}
      <RightCard>
        <h1 className="text-2xl font-semibold tracking-tight">
          {confirmSignUpTitle}
        </h1>

        <ConfirmSignUpForm
          loading={loading}
          onSubmit={handleConfirmSignUp}
          defaultValues={{
            email: decodeURIComponent(params.get('email') || ''),
          }}
        />
        <div className="flex gap-1 justify-center leading-tight">
          <p className="text-sm text-muted-foreground">{signUpCTAQuestion}</p>
          <Link
            to={`${props.loginRoute}${
              params.size > 0 ? '?' + params.toString() : ''
            }`}
            className="text-sm text-primary leading-tight"
          >
            {signUpCTAText}
          </Link>
        </div>
      </RightCard>
      {referralCode && <RefereeBottomCard />}
    </>
  );
};
